// ============================================
// Admin Login Page — Dark Premium Admin-Branded
// Uses strict hyper-glassmorphism (aurora, rounded-3xl, crisp borders)
// URL: /admin/login
// ============================================

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUser, clearError } from '../../redux/slices/authSlice';
import { useTheme } from '../../context/ThemeContext';


const AdminLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading, error, user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [localError, setLocalError] = useState('');

    useEffect(() => {
        if (isAuthenticated && user) {
            if (user.role === 'admin') {
                navigate('/admin', { replace: true });
            } else {
                const routes = { dispatcher: '/dispatcher/login', driver: '/login', user: '/login' };
                navigate(routes[user.role] || '/login', { replace: true });
            }
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => () => dispatch(clearError()), [dispatch]);

    const handleChange = (e) => {
        setLocalError('');
        if (error) dispatch(clearError());
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLocalError('');
        dispatch(clearError());

        if (!formData.email.trim()) {
            return setLocalError('Please provide your admin email.');
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            return setLocalError("Please enter a valid email address.");
        }
        if (!formData.password) {
            return setLocalError('Please enter your password.');
        }

        dispatch(loginUser({ ...formData, loginRole: 'admin' }));
    };

    return (
        <div className="h-screen w-screen flex bg-slate-50 dark:bg-[#050510] font-sans transition-colors duration-500 overflow-hidden aurora-bg relative">
            
            {/* ── Left Panel: Admin Branding ────────── */}
            <div className="hidden lg:flex lg:w-[480px] xl:w-[560px] relative overflow-hidden bg-gradient-to-br from-slate-950 via-rose-950 to-slate-900 border-r border-white/5">
                {/* Animated Background Blobs */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 -left-20 w-72 h-72 bg-rose-500/10 rounded-full blur-3xl animate-pulse" />
                    <div className="absolute bottom-32 -right-16 w-96 h-96 bg-orange-500/8 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
                    <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-indigo-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />

                    {/* Grid Pattern */}
                    <div className="absolute inset-0 opacity-[0.03]"
                        style={{
                            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                            backgroundSize: '60px 60px'
                        }}
                    />

                    {/* Admin Shield SVG Overlay */}
                    <svg className="absolute inset-0 w-full h-full opacity-[0.04]" viewBox="0 0 560 900">
                        <path d="M280 150 L400 200 V400 C400 550 280 650 280 650 C280 650 160 550 160 400 V200 L280 150 Z" stroke="url(#adminGrad)" strokeWidth="1.5" fill="none" />
                        <circle cx="280" cy="400" r="180" stroke="url(#adminGrad)" strokeWidth="1" fill="none" strokeDasharray="8 4" />
                        <circle cx="280" cy="400" r="260" stroke="url(#adminGrad)" strokeWidth="0.8" fill="none" strokeDasharray="4 6" />
                        <defs>
                            <linearGradient id="adminGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stopColor="#f43f5e" />
                                <stop offset="100%" stopColor="#fb923c" />
                            </linearGradient>
                        </defs>
                    </svg>
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col justify-between p-12 w-full">
                    {/* Logo */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-orange-600 flex items-center justify-center shadow-lg shadow-rose-500/25">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>
                        <div>
                            <span className="text-white/90 font-bold text-xl tracking-tight">IdaCab</span>
                            <span className="block text-rose-400/80 text-xs font-semibold tracking-widest uppercase">Admin Control</span>
                        </div>
                    </div>

                    {/* Hero */}
                    <div className="my-auto">
                        <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
                            Global
                            <span className="block mt-2 bg-gradient-to-r from-rose-400 via-orange-400 to-amber-400 bg-clip-text text-transparent">
                                Administration
                            </span>
                            Dashboard.
                        </h1>
                        <p className="text-slate-400 text-lg leading-relaxed max-w-md">
                            Comprehensive oversight of fleet logistics, system analytics, and security management.
                        </p>

                        <div className="flex flex-wrap gap-3 mt-8">
                            {['Dashboard', 'User Access', 'Audit Logs', 'Fleet Management'].map((f, i) => (
                                <span key={i} className="px-3 py-1.5 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300/80 text-xs font-medium uppercase tracking-wider">
                                    {f}
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="text-slate-600 text-xs font-medium uppercase tracking-widest">
                        © 2026 IdaCab — Administrative Access Only
                    </div>
                </div>
            </div>

            {/* ── Right Panel: Next-Gen Login Form ──────────────── */}
            <div className="flex-1 h-full relative selection:bg-rose-500/20 z-10 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-20 overflow-hidden">
                
                {/* Global Theme Toggle */}
                <div className="absolute top-6 right-6 z-50">
                    <button
                        onClick={toggleTheme}
                        className="p-3 rounded-full bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 shadow-sm"
                        aria-label="Toggle theme"
                    >
                        {theme === 'light' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        )}
                    </button>
                </div>

                <div className="w-full max-w-[460px] bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] p-6 sm:p-10 shadow-2xl shadow-slate-200 dark:shadow-black/50 border border-slate-100 dark:border-white/5 transition-colors duration-500 relative mt-4 mb-4 sm:my-0">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-orange-600 flex items-center justify-center p-2">
                             <svg className="w-full h-full text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                        </div>
                        <div>
                            <span className="font-bold text-xl text-slate-800 dark:text-white">IdaCab</span>
                            <span className="block text-rose-600 dark:text-rose-400 text-xs font-semibold uppercase tracking-widest">Admin</span>
                        </div>
                    </div>

                    {/* Admin Badge */}
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-50 dark:bg-rose-500/10 border border-rose-100 dark:border-rose-500/20 mb-6 transition-colors">
                        <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                        <span className="text-xs font-semibold text-rose-700 dark:text-rose-400 uppercase tracking-widest">Admin Portal</span>
                    </div>

                    <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1 transition-colors">Secure Sign In</h2>
                    <p className="text-slate-500 dark:text-slate-400 text-sm mb-8 transition-colors">Enter your credentials to continue.</p>

                    {/* Error */}
                    {(error || localError) && (
                        <div className="mb-6 flex items-start gap-3 p-3.5 rounded-xl bg-rose-50/80 dark:bg-rose-500/10 border border-rose-200/80 dark:border-rose-500/20 backdrop-blur-sm transition-all duration-300 animate-in fade-in slide-in-from-top-2">
                            <div className="mt-0.5 text-rose-600 dark:text-rose-400 flex-shrink-0">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <p className="text-rose-700 dark:text-rose-300 text-[13px] font-semibold leading-snug tracking-wide">
                                {localError || (error === 'Login failed' ? 'Invalid email or password' : error)}
                            </p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Email */}
                        <div>
                            <label className="block text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 ml-1 transition-colors">Admin Email</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                <input
                                    name="email" type="text" value={formData.email} onChange={handleChange}
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all text-sm backdrop-blur-sm shadow-inner"
                                    placeholder="admin@idacab.com"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 ml-1 transition-colors">Password</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <svg className="w-5 h-5 text-slate-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <input
                                    name="password" type={showPassword ? 'text' : 'password'} value={formData.password} onChange={handleChange}
                                    className="w-full pl-12 pr-12 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all text-sm backdrop-blur-sm shadow-inner"
                                    placeholder="Enter admin password"
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                                    {showPassword ? (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Submit */}
                        <button type="submit" disabled={isLoading}
                            className="w-full py-4 px-4 rounded-2xl bg-gradient-to-r from-rose-600 to-orange-600 hover:from-rose-700 hover:to-orange-700 text-white font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-rose-500/25 hover:shadow-rose-500/40 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-rose-500/20 active:scale-[0.98]">
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                                    CONNECTING...
                                </>
                            ) : (
                                'SIGN IN'
                            )}
                        </button>
                    </form>

                    <p className="text-center text-[10px] font-bold text-slate-400 dark:text-slate-500 mt-8 transition-colors tracking-widest uppercase">
                         Authorized administrative access only.<br />
                         Secure portal active.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;
